import { useState, useEffect } from 'react'
import { AlertCircle, FileText, Loader2 } from 'lucide-react'
import { Modal } from '@atrvd/saas-ui'
import { PaginationControls } from './PaginationControls'
import { formatYMDtoMDY } from './format-utils'
import { useFileImportTransactions, useFileImportTransactionsCount } from '@/hooks/useBanking'
import { cn } from '@/lib/utils'
import { DEFAULT_DETAILS_PAGE_SIZE } from '@/types/banking'
import type { FileImportRow } from '@/types/banking'
import { formatCategoryAmount } from '@dcp/shared/utils/formatters'

// `formatCategoryAmount` is the single source of truth for rendering a
// category-aware transaction amount (text + Tailwind className). Lives in
// `@dcp/shared/utils/formatters` so this modal and ManualTransactionsTable
// can't drift on sign / locale / parens formatting independently. See its
// docblock for the full DCP-124 contract.

/**
 * Modal showing paginated transactions for a specific file import.
 * Displays transaction date, description, amount, facility, bank account, and category.
 *
 * @param fileImport - The file import record to show transactions for (null = closed)
 * @param onClose - Callback to close the modal
 */
export function TransactionImportDetailsModal({
  fileImport,
  onClose,
}: {
  fileImport: FileImportRow | null
  onClose: () => void
}) {
  const [page, setPage] = useState(0)

  // Reset page when a different import is selected
  useEffect(() => {
    setPage(0)
  }, [fileImport?.id])

  const {
    data: transactions,
    isLoading,
    isError,
    error,
  } = useFileImportTransactions(
    fileImport?.id ?? null,
    { limit: DEFAULT_DETAILS_PAGE_SIZE, offset: page * DEFAULT_DETAILS_PAGE_SIZE },
  )
  const {
    data: txTotal = 0,
    isError: isCountError,
    error: countError,
  } = useFileImportTransactionsCount(fileImport?.id ?? null)
  const totalPages = Math.ceil(txTotal / DEFAULT_DETAILS_PAGE_SIZE)
  // Surface either query's error — the rows query is the user-visible
  // one but count failures (Math.ceil(0/N) = 0) would otherwise render
  // as "0 transactions" with the spinner stuck, which masks the real
  // problem.
  const hasError = isError || isCountError
  const displayError = error ?? countError

  return (
    <Modal
      isOpen={!!fileImport}
      onClose={onClose}
      title={`Imported Transactions — ${fileImport?.file_name ?? ''}`}
      size="xl"
      className="!max-w-[1100px]"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {txTotal} transaction{txTotal !== 1 ? 's' : ''} imported from this file
        </p>

        {hasError ? (
          <div className="flex flex-col items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p className="text-sm">
              Failed to load transactions
              {displayError instanceof Error ? `: ${displayError.message}` : ''}
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !transactions?.length ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <FileText className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No transactions found</p>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border max-h-[460px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 sticky top-0 z-10">
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Date</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Description</th>
                  <th className="px-3 py-2 text-right font-medium bg-muted/50">Amount</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Facility</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Account</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Category</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {transactions.map((tx) => {
                  const amt = formatCategoryAmount(tx.amount, tx.categories?.type)
                  const dateStr = formatYMDtoMDY(tx.transaction_date)

                  return (
                    <tr key={tx.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2 whitespace-nowrap">{dateStr}</td>
                      <td className="px-3 py-2 max-w-[250px] truncate" title={tx.description}>
                        {tx.description}
                      </td>
                      <td className={cn('px-3 py-2 text-right whitespace-nowrap font-medium', amt.className)}>
                        {amt.text}
                      </td>
                      {/* DCP-324: the facility is the linked ACCOUNT's — a
                          transaction carries none of its own. An imported row
                          whose account is unassigned shows "-" here and still
                          appears: this is the import's audit view, so it lists
                          everything the import produced. */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        {tx.bank_accounts?.facilities?.name ?? '-'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {tx.bank_accounts?.name ?? '-'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {tx.categories?.name ?? '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </Modal>
  )
}
